import React from 'react';
import { Button } from 'antd';
import { EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';

import { useCurrentUser } from '../../models/useCurrentUser';
import { useClientForOwner } from '../../models/useClientForOwner';

import { navigateToClient } from './DelegatedClientsGrid';
import { useClientDetails } from './useClientDetails';

/**
 * Always-visible reminder that you are inside a client you MANAGE, not your own —
 * with the way back one click away. Mounted in AccountProfileLayout so it rides
 * every page of the visited client, not just its home.
 *
 * Shows only when the loaded client has an owner and it isn't you (the delegated
 * case — team-owned clients have no ownerId and never show it). A pure delegate
 * with no client of their own gets the notice without the back button.
 */
export const DelegatedViewingBanner = ({ client: clientProp, clientId = null }) => {
  const [, authUser] = useCurrentUser();
  const uid = authUser ? authUser.uid : null;
  // Layouts that already load the client pass it; layouts that don't (TeamLayout)
  // pass clientId and the banner loads it itself — it must ride EVERY page of a
  // visited client, whichever layout that page uses.
  const loadedClient = useClientDetails(clientProp ? null : clientId);
  const client = clientProp || loadedClient;
  const isVisiting = !!(client && uid && client.ownerId && client.ownerId !== uid);
  const [, ownClient] = useClientForOwner(isVisiting ? uid : null);

  if (!isVisiting) return null;

  return (
    <div
      data-testid="delegated-viewing-banner"
      style={{
        position: 'sticky', top: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: '8px 16px', background: '#fff0f8', borderBottom: '2px solid #ff0098',
        fontSize: 14,
      }}
    >
      <span>
        <EyeOutlined style={{ marginRight: 6 }} />
        You&apos;re viewing <strong>{client.displayName || 'a client'}</strong> — a client you have access to.
      </span>
      {ownClient && ownClient.id && (
        <Button
          size="small"
          data-testid="switch-back-button"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigateToClient(ownClient.id)}
        >
          Back to my account
        </Button>
      )}
    </div>
  );
};

export default DelegatedViewingBanner;
